import { PricingModel } from './types';

const visitsCountModelFeatures: PlanFeature[] = [
  {
    title: 'AI powered accessibility',
    info: "Maintain your website's accessibility with continuous monitoring and automatic fixes for common issues in accordance to WCAG 2.1 standards.",
  },
  {
    title: 'Compliance for ADA, AODA, EAA',
    info: 'Meet web accessibility requirements in accordance to WCAG 2.1 standards.',
  },
  {
    title: 'Automated proof of effort',
    info: 'Documentation to help you and your legal team. You have access to monthly audits and remediation reports to show your proof of effort toward making your site accessible.',
  },
  {
    title: 'Customer support',
    info: 'Get instant answers during business hours to your question via live chat.',
  },
  {
    title: 'First response SLA',
    info: 'Get the first response to your support inquiry between 24-48 hours, depending on your plan.',
  },
  {
    title: 'Detailed remediation report',
    info: "Get an in-depth breakdown of the accessibility fixes applied to maintain your website's accessibility status.",
  },
  {
    title: 'Litigation support package',
    info: 'A dedicated accessibility expert will be assigned to get you the information you need to help you address a demand letter or lawsuit—until the case is resolved. For Scale (1 hr) and Enterprise (5-10 hr), consultation with an Attorney specializing in ADA compliance.',
  },
  {
    title: 'Monthly accessibility impact report',
    info: 'A monthly report showing how accessWidget impacts accessibility on your website plus 1 hour with an accessibility expert.',
  },
  {
    title: 'Google Analytics',
    info: 'View and track accessWidget’s user interaction as events in Google Analytics.',
  },
  {
    title: 'Multi-account management',
    info: 'Create and manage multiple dashboard accounts under one subscription.',
  },
  {
    title: 'User & team management',
    info: 'Secure multi-member participation in your account dashboard with predefined roles and privileges.',
  },
  {
    title: 'APIs & batch management ',
    info: 'Easily manage multiple licenses with accessiBe’s APIs.',
  },
  {
    title: 'Manual testing',
    info: 'A manual inspection of the most important functionalities of key pages on your website (e.g., checkout process) to identify critical accessibility barriers. For Enterprise plans, in addition to yearly testing, you can request another manual test to be carried out on demand.',
  },
  {
    title: 'Custom fixes to improve accessibility conformance',
    info: "Based on the insights gathered during the manual test, accessiBe developers will customize your widget to address your website's unique accessibility needs.",
  },
  {
    title: 'Custom code fixes',
    info: "If accessibility remediation is needed at the code level, we'll work to provide a custom fix.",
  },
  {
    title: 'Single sign-on (SSO)',
    info: 'Let managers and team members log into the portal using single sign-on credentials.',
  },
  {
    title: 'Dedicated account manager',
    info: "Get a dedicated account manager to answer questions you have regarding your website's accessibility status.",
  },
  {
    title: 'Custom legal terms',
  },
];

const pageCountModelFeatures: PlanFeature[] = [
  {
    title: 'AI-powered daily accessibility monitoring and audits',
  },
  {
    title: 'Accessibility statement & remediation efforts',
  },
  {
    title: 'Monthly accessibility audit reports',
  },
  {
    title: 'Litigation support package',
    info: 'A dedicated accessibility expert will be assigned to get you the information you need to help you address a demand letter or lawsuit—until the case is resolved. For Scale (1 hr) and Enterprise (5-10 hr), consultation with an Attorney specializing in ADA compliance.',
  },
  {
    title: 'Monthly remediation report with full documentation',
  },
  {
    title: 'Google Analytics Integration',
  },
  {
    title: 'Advanced account management',
    info: 'Manage users in your team and create multiple accounts to manage multiple licenses separately.',
  },
  {
    title: 'Priority Support & SLA',
    info: 'Gain direct access to customer experts and enjoy priority \nsupport with shorter SLAs.',
  },
  {
    title: 'Single-Sign-On (SSO)',
  },
  {
    title: 'Solution engineering',
    info: 'Get custom, tailored account or feature developments and solutions as your business might require.',
  },
  {
    title: 'Custom legal terms',
  },
  {
    title: 'Testing accounts',
    info: 'Leverage the ability to install accessWidget on non-production environments with no extra cost.',
  },
];

type PlanDetails = {
  pricingModel?: PricingModel;
  features: number[];
  specialValues?: { [featureIndex: number]: string };
};

export type PlanFeature = {
  title: string;
  info?: string;
  value?: string;
};

const planDetails: { [key: string]: PlanDetails } = {
  [`${PricingModel.visitCount}-1`]: {
    pricingModel: PricingModel.visitCount,
    features: [0, 1, 2, 3, 4, 5],
    specialValues: {
      4: '48hr',
    },
  },
  [`${PricingModel.visitCount}-2`]: {
    pricingModel: PricingModel.visitCount,
    features: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    specialValues: {
      4: '48hr',
    },
  },
  [`${PricingModel.visitCount}-3`]: {
    pricingModel: PricingModel.visitCount,
    features: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
    specialValues: {
      4: '48hr',
      12: 'Yearly',
    },
  },
  [`${PricingModel.visitCount}-enterprise`]: {
    pricingModel: PricingModel.visitCount,
    features: Array.from(
      { length: visitsCountModelFeatures.length },
      (_, i) => i,
    ),
    specialValues: {
      4: '24hr',
      12: 'Yearly + Custom',
    },
  },
  [`${PricingModel.pageCount}-enterprise`]: {
    pricingModel: PricingModel.pageCount,
    features: Array.from(
      { length: pageCountModelFeatures.length },
      (_, i) => i,
    ),
  },
  [`${PricingModel.pageCount}-1`]: {
    pricingModel: PricingModel.pageCount,
    features: [0, 1, 2, 3],
  },
  [`${PricingModel.pageCount}-2`]: {
    pricingModel: PricingModel.pageCount,
    features: [0, 1, 2, 3, 4, 5, 6, 7],
  },
  [`${PricingModel.pageCount}-3`]: {
    pricingModel: PricingModel.pageCount,
    features: [0, 1, 2, 3, 4, 5, 6, 7],
  },
};

export const maximumFeaturesCount = (pricingModel: string) => {
  const featuresList =
    pricingModel === PricingModel.visitCount
      ? visitsCountModelFeatures
      : pageCountModelFeatures;
  return featuresList.length;
};

export const allFeaturesArray = (pricingModel: string): PlanFeature[] => {
  const featuresList =
    pricingModel === PricingModel.visitCount
      ? visitsCountModelFeatures
      : pageCountModelFeatures;
  return featuresList;
};

export const planFeaturesIndexMap = (
  planTier: number,
  pricingModel: string,
  isEnterprise: boolean,
): { [key: number]: PlanFeature } => {
  const featuresList =
    pricingModel === PricingModel.visitCount
      ? visitsCountModelFeatures
      : pageCountModelFeatures;
  const plan = isEnterprise
    ? planDetails[`${pricingModel}-enterprise`]
    : planDetails[`${pricingModel}-${planTier}`] ?? { features: [] };

  const featuresMap: { [key: number]: PlanFeature } = {};
  plan.features.map((featureIndex) => {
    const feature = featuresList[featureIndex];

    if (plan.specialValues && plan.specialValues[featureIndex]) {
      feature.value = plan.specialValues[featureIndex];
    }
    featuresMap[featureIndex] = feature;
  });
  return featuresMap;
};
